/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import Yup from "yup";
import { bool, func, shape, string } from "prop-types";

import { Login } from "@ui-kit/components/index.es";
import { actions as fingerprintActions } from "reducers/fingerprint";
import { actions as layoutActions } from "reducers/layout";
import { actions as loginActions, selectors as loginSelectors } from "reducers/login";
import { actions as recoveryPasswordActions } from "reducers/recoveryPassword";
import { actions as statusActions } from "reducers/status";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import * as i18n from "util/i18n";
import * as configUtil from "util/config";
import openExternalUrl from "util/openExternalUrl";
import Captcha from "pages/_components/fields/credentials/Captcha";
import { LOGIN_LABELS_KEYS, LOGIN_TEXT_KEYS } from "./loginI18nKeys";
import documentTypeFormats from "../../utils/documentTypeFormats";

const DOC_TYPES = [
    { value: "DNI", label: "DNI" },
    { value: "CUIT", label: "CUIT" },
    { value: "CUIL", label: "CUIL" },
    { value: "CDI", label: "CDI" },
];

function LoginStep1V3({
    dispatch,
    location,
    rememberedUser,
    showCaptcha,
    resetCaptcha,
    isSubmitting,
    fingerprintInitialized,
    biometricLogin,
    statusTokenPass,
    userIdToken,
    isTokenSeedAvailable,
    recoveringSeedError,
    fieldErrors,
}) {
    const formikRef = useRef(null);
    const [isFormReady, setIsFormReady] = useState(false);
    const [isAutoLoginFlow, setIsAutoLoginFlow] = useState(false);
    const [allowAutoLoginFromBiometric, setAllowAutoLoginFromBiometric] = useState(false);
    const autoLoginTriggeredRef = useRef(false);
    const rememberedDocument = rememberedUser?.document ?? "";
    const rememberedDocumentType = rememberedUser?.documentType ?? "DNI";
    const rememberedUsername = rememberedUser?.userpassUsername ?? "";
    const hasRememberedDocument = Boolean(rememberedDocument && rememberedDocumentType);
    const hasRememberedUsername = Boolean(rememberedUsername);
    const hasUser = hasRememberedDocument && hasRememberedUsername;
    const isMobileNative = Boolean(window.isMobileApp && window.isMobileApp());
    const fingerprintEnabled = configUtil.getBoolean("frontend.login.fingerprint.enabled");
    const useOfflineToken = configUtil.getBoolean("backend.show.Token.offline");
    const useBiometricTokenAccess = configUtil.getBoolean("backend.show.Biometric.Token.offline");
    const showBiometricLogin = Boolean(
        isMobileNative &&
            fingerprintEnabled &&
            rememberedDocument &&
            biometricLogin?.document === rememberedDocument &&
            biometricLogin?.showLogin,
    );
    const hasKnownUser = showBiometricLogin ? hasUser || hasRememberedDocument : hasUser;
    const showUsePasswordButton = Boolean(showBiometricLogin && rememberedUser?.usernameFromRememberedUser === true);
    const tokenActiveInDevice = statusTokenPass === "active" && !!userIdToken;
    const showTokenButton = tokenActiveInDevice && (!useOfflineToken || !!isTokenSeedAvailable);

    useEffect(() => {
        if (
            window.isMobileApp &&
            window.isMobileApp() &&
            window.common &&
            typeof window.common.hideSplashScreen === "function"
        ) {
            window.common.hideSplashScreen();
        }
    }, []);

    useEffect(() => {
        if (!biometricLogin) {
            dispatch(fingerprintActions.fingerprintGetInitialData());
        }
    }, [dispatch, biometricLogin]);

    useEffect(() => {
        if (isMobileNative) {
            dispatch(tokenActions.verifyTokenSeedExist());
        }
    }, [dispatch, isMobileNative]);

    useEffect(() => {
        dispatch(layoutActions.setScopes(["login"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(loginActions.resetLoading());
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(statusActions.saveLastHref({ location }));
    }, [dispatch, location]);

    const initialValues = useMemo(
        () => ({
            document: {
                number: rememberedDocument,
                type: { value: rememberedDocumentType, label: rememberedDocumentType },
            },
            user: rememberedUsername,
            rememberDocumentUser: hasRememberedDocument,
        }),
        [rememberedDocument, rememberedDocumentType, rememberedUsername, hasRememberedDocument],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                document: Yup.object().shape({
                    number: hasUser ? Yup.string() : Yup.string().required(i18n.get("login.step1.document.required")),
                    type: Yup.mixed(),
                }),
                user: hasUser ? Yup.string() : Yup.string().required(i18n.get("login.step1.username.required")),
                captcha: showCaptcha ? Yup.string().required(i18n.get("login.step1.captcha.required")) : Yup.string(),
            }),
        [showCaptcha, hasUser],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const docType = values.document?.type?.value ?? values.document?.type ?? "DNI";
            const document = values.document?.number ?? values.document ?? "";
            const username = values.user ?? "";
            const rememberDocument = values.rememberDocumentUser ?? false;
            const captcha = values.captcha ?? "";
            setIsAutoLoginFlow(false);
            dispatch(loginActions.setResetCaptcha(false));
            dispatch(
                loginActions.loginStep1(username, rememberDocument, document, docType, captcha, {
                    ...formikBag,
                    values,
                }),
            );
        },
        [dispatch],
    );

    useEffect(() => {
        if (
            ((showBiometricLogin && !allowAutoLoginFromBiometric) ||
                !hasUser ||
                autoLoginTriggeredRef.current ||
                (!isFormReady && !isMobileNative) ||
                (!formikRef.current && !isMobileNative)) &&
            isMobileNative
        ) {
            return;
        }

        const formikBag = formikRef.current;
        const formatValidation = documentTypeFormats(rememberedDocumentType, rememberedDocument);
        if (!formatValidation) {
            return;
        }

        autoLoginTriggeredRef.current = true;
        setIsAutoLoginFlow(true);
        dispatch(loginActions.setResetCaptcha(false));
        dispatch(
            loginActions.loginStep1(
                rememberedUsername,
                true,
                rememberedDocument,
                rememberedDocumentType,
                "",
                formikBag,
                null,
                true,
            ),
        );
    }, [
        dispatch,
        showBiometricLogin,
        allowAutoLoginFromBiometric,
        hasUser,
        isFormReady,
        rememberedDocument,
        rememberedDocumentType,
        rememberedUsername,
    ]);

    useEffect(() => {
        if (!isSubmitting && isAutoLoginFlow) {
            setIsAutoLoginFlow(false);
        }
    }, [isSubmitting, isAutoLoginFlow]);

    const handleRecoverUser = useCallback(
        (_, formikProps) => {
            const values = formikProps?.values;
            const docType = values?.document?.type?.value ?? values?.document?.type ?? "DNI";
            const document = values?.document?.number ?? values?.document ?? "";
            dispatch(recoveryPasswordActions.recoveryUserpassPre(document, docType));
            dispatch(
                push({
                    pathname: "/v3/recoveryUserpass/step0",
                    state: {
                        document,
                        documentType: docType,
                    },
                }),
            );
        },
        [dispatch],
    );

    const handleRequestAccount = useCallback(() => {
        dispatch(push("/noUser"));
    }, [dispatch]);

    const handleSimulate = useCallback(() => {
        const url = configUtil.get("plazoFijoWeb.URL.PaginaCMF");
        openExternalUrl(url);
    }, []);

    const handleChangeLoggedUser = useCallback(() => {
        dispatch(loginActions.removeRememberedUser());
    }, [dispatch]);

    const handleBiometricLogin = useCallback(() => {
        dispatch(loginActions.fingerprintLoginPre());
    }, [dispatch, showBiometricLogin, biometricLogin]);

    const handleUsePasswordLogin = useCallback(() => {
        setAllowAutoLoginFromBiometric(true);
    }, []);

    const handleViewOTPToken = useCallback(() => {
        if (useBiometricTokenAccess && useOfflineToken) {
            dispatch(tokenActions.biometricTokenAccess());
        } else {
            dispatch(push("/tokenPassword"));
        }
    }, [dispatch, useBiometricTokenAccess, useOfflineToken, statusTokenPass, userIdToken, isTokenSeedAvailable]);

    const enableSimulate =
        configUtil.getBoolean("plazoFijoWeb.Funcion.Activa") && configUtil.getBoolean("frontend.show.plazoFijoWeb");

    const getLabels = useCallback(
        (id) => {
            if (id === "simulate" && enableSimulate) {
                const configLabel = configUtil.get("plazoFijoWeb.Boton.Nombre");
                if (configLabel && configLabel.toLowerCase() !== "plazo fijo web") {
                    return configLabel;
                }
                return i18n.get("login.step1.simulate.label");
            }
            return i18n.get(LOGIN_LABELS_KEYS[id] ?? id);
        },
        [enableSimulate],
    );
    const getText = useCallback((id) => i18n.get(LOGIN_TEXT_KEYS[id] ?? id), []);

    const welcomeText = useMemo(() => {
        if (!hasKnownUser || !rememberedUser) {
            return undefined;
        }
        const userName = rememberedUser.userFirstName ?? "";
        return i18n.get("login.step1.welcomeWithUser", `Hola, ${userName}`, { USER_NAME: userName });
    }, [hasKnownUser, rememberedUser]);

    const rememberCheckboxLabelKey = hasRememberedDocument ? "rememberUser" : "rememberDocumentUser";
    const showNoAccount = !hasUser && !showBiometricLogin;
    const showBiometricGlobalLoading = fingerprintInitialized && isSubmitting;

    const additionalInputs = useMemo(() => {
        if (!showCaptcha) {
            return [];
        }
        return [
            {
                id: "captcha",
                colProps: { size: { xs: 12 } },
                isField: true,
                customElement: (fieldProps) => <Captcha {...fieldProps} resetCaptcha={resetCaptcha} />,
            },
        ];
    }, [showCaptcha, resetCaptcha]);

    return (
        <Login
            key="loginStep1"
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            handleRecoverUser={handleRecoverUser}
            handleRequestAccount={handleRequestAccount}
            handleSimulate={handleSimulate}
            getLabels={getLabels}
            getText={getText}
            step={1}
            hasUser={hasUser}
            enableBiometric={showBiometricLogin}
            showUsePasswordButton={showUsePasswordButton}
            handleBiometricLogin={handleBiometricLogin}
            handleUsePasswordLogin={handleUsePasswordLogin}
            enableToken={showTokenButton}
            handleViewOTPToken={handleViewOTPToken}
            enableSimulate={enableSimulate}
            enableChangeUser={hasKnownUser}
            handleChangeLoggedUser={handleChangeLoggedUser}
            documentTypeOptions={DOC_TYPES}
            additionalInputs={additionalInputs}
            welcomeText={welcomeText}
            rememberCheckboxLabelKey={rememberCheckboxLabelKey}
            showNoAccount={showNoAccount}
            showSimulate={enableSimulate}
            submitLoading={isSubmitting}
            globalLoading={(isAutoLoginFlow && isSubmitting) || showBiometricGlobalLoading || recoveringSeedError}
            onFormReady={(formik) => {
                formikRef.current = formik;
                setIsFormReady(true);
            }}
            fieldErrors={fieldErrors}
        />
    );
}

LoginStep1V3.propTypes = {
    dispatch: func.isRequired,
    location: shape({}).isRequired,
    rememberedUser: shape({}),
    showCaptcha: bool.isRequired,
    resetCaptcha: bool.isRequired,
    isSubmitting: bool.isRequired,
    fingerprintInitialized: bool.isRequired,
    biometricLogin: shape({}),
    statusTokenPass: string,
    userIdToken: string,
    isTokenSeedAvailable: bool,
    recoveringSeedError: bool.isRequired,
    fieldErrors: shape({}),
};

LoginStep1V3.defaultProps = {
    rememberedUser: null,
    biometricLogin: null,
    statusTokenPass: "",
    userIdToken: "",
    isTokenSeedAvailable: false,
    fieldErrors: null,
};

const mapStateToProps = (state) => ({
    rememberedUser: loginSelectors.getRememberedUser(state),
    showCaptcha: loginSelectors.getShowCaptcha(state),
    resetCaptcha: loginSelectors.getResetCaptcha(state),
    isSubmitting: loginSelectors.getFetching(state),
    fingerprintInitialized: loginSelectors.getFingerprintInitialized(state),
    biometricLogin: loginSelectors.getBiometricLogin(state),
    statusTokenPass: tokenSelectors.getStatusTokenPass(state),
    userIdToken: tokenSelectors.getUserIdToken(state),
    isTokenSeedAvailable: tokenSelectors.getIsTokenSeedAvailable(state),
    recoveringSeedError: tokenSelectors.getRecoveringSeedError(state),
    fieldErrors: loginSelectors.getFieldErrors(state),
});

export default connect(mapStateToProps)(LoginStep1V3);
